import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import React, { FC, useEffect, useRef } from "react";
import styled from "styled-components";
import { getPercentageValue } from "lib/utils/table/getPercentageValue";
import { Allocation } from "../types";

type Props = {
  item: Allocation;
  className?: string;
  isOpen: boolean;
  onOpenModal: () => void;
};

export const TableRowBottomLine: FC<Props> = withErrorBoundary(({ item, className = "", isOpen, onOpenModal }) => {
  const { allocation, fund } = item ?? {};
  const tableRowBottomLine = useRef<HTMLDivElement | null>(null);
  const heightTableRowBottomLine = useRef(0);

  useEffect(() => {
    heightTableRowBottomLine.current = tableRowBottomLine.current?.getBoundingClientRect().height ?? 0;
  }, []);

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenModal();
  };

  return (
    <StyledTableRowBottomLine
      className={cn(className, {})}
      style={{ height: `${isOpen ? heightTableRowBottomLine.current : 0}px` }}
    >
      <div className="Table__YearsReturnRow Font_12_16_500" ref={tableRowBottomLine}>
        <div className="Table__arrowColumn" />
        <div className="TableRowCell__assetClassColumn Color_dusk50">
          <span onClick={handleOpenModal} className="TableRowCell__assetClassColumnModalOpen">
            {fund?.symbol}
          </span>
        </div>
        <div className="TableRowCell TableRowCell__Allocation">{getPercentageValue(allocation?.scalar * 100)}</div>
        <div className="TableRowCell">{getPercentageValue(fund?.one_year_return?.scalar * 100)}</div>
        <div className="TableRowCell">{getPercentageValue(fund?.five_year_return.scalar * 100)}</div>
        <div className="TableRowCell">{getPercentageValue(fund?.ten_year_return?.scalar * 100)}</div>
        <div className="TableRowCell">{getPercentageValue(fund?.ytd_return?.scalar * 100)}</div>
      </div>
    </StyledTableRowBottomLine>
  );
}, {});

const StyledTableRowBottomLine = styled.div`
  overflow: hidden;
  transition: 0.2s;

  .TableRowBottomLine {
    display: grid;
    grid-template-columns: 32px 2fr;
    grid-auto-columns: 1fr; /* Define the width of subsequent columns */
    grid-auto-flow: column; /* Arrange items in columns */
    width: 100%;
    align-items: center;
    text-align: right;
  }

  .TableRowCell__assetClassColumnModalOpen {
    text-decoration: underline;
  }

  .TableRowCell__assetClassColumn {
    text-align: left;
  }

  .TableRowCell {
    position: relative;
  }

  .TableRowCell:empty::before {
    content: "";
    display: inline-block;
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    width: 12px;
    height: 1px;
    background: ${({ theme }) => theme.colors.fog["80"]};
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Table__YearsReturnRow > .TableRowCell__Allocation {
      display: none;
    }

    .TableRowBottomLine {
      grid-template-columns: 24px 2fr;
    }

    .TableRowCell:empty::before {
      margin-right: 2px;
    }
  }
`;
